import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Wifi,
  Zap,
  ChefHat,
  Shield,
  Tv,
  Wind,
  ChevronDown,
  MapPin,
  Phone,
  Clock
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [suites, setSuites] = useState([]);

  useEffect(() => {
    // Fetch suites data
    fetch(`${API}/suites`)
      .then((res) => res.json())
      .then((data) => setSuites(data.slice(0, 3)))
      .catch((err) => console.error('Error fetching suites:', err));
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: 'easeOut' }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  // Section 1: Hero
  const HeroSection = () => (
    <section
      data-testid="hero-section"
      className="relative h-screen w-full flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_azraa-luxury/artifacts/6uek5qa5_image.png"
          alt="Luxury living room"
          className="w-full h-full object-cover"
          data-testid="hero-background-image"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-5xl">
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }}
          data-testid="hero-headline"
          className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-none font-light text-white mb-6"
        >
          Where Comfort Becomes a Standard.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4 }}
          data-testid="hero-subheadline"
          className="text-base sm:text-lg leading-relaxed text-white/90 mb-8 max-w-3xl mx-auto"
        >
          Premium serviced apartments in the heart of Awka — fully equipped, beautifully designed, and ready for you tonight.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.6 }}
        >
          <Link
            to="/book"
            data-testid="hero-cta-button"
            className="inline-block bg-[#1A1A1A] text-[#FAF8F5] hover:bg-[#C9A84C] hover:text-[#1A1A1A] transition-colors duration-300 px-12 py-4 text-sm tracking-widest uppercase"
          >
            Reserve Your Suite
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="absolute bottom-12 left-1/2 transform -translate-x-1/2"
          data-testid="scroll-indicator"
        >
          <ChevronDown className="text-white animate-bounce" size={32} strokeWidth={1} />
        </motion.div>
      </div>
    </section>
  );

  // Section 2: Trust Strip
  const TrustStrip = () => {
    const features = [
      { icon: Zap, text: '24-Hour Power Supply' },
      { icon: Wifi, text: 'Free High-Speed WiFi' },
      { icon: ChefHat, text: 'Fully Equipped Kitchen' },
      { icon: Shield, text: '24-Hour Security' }
    ];

    return (
      <section
        data-testid="trust-strip-section"
        className="bg-[#FAF8F5] border-y border-[#1A1A1A]/10 py-6"
      >
        <div className="px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`trust-feature-${index}`}
                className="flex items-center justify-center gap-3"
              >
                <feature.icon className="text-[#C9A84C]" size={20} strokeWidth={1.5} />
                <span className="text-xs sm:text-sm text-[#1A1A1A] font-medium">{feature.text}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Section 3: Featured Suites
  const FeaturedSuites = () => (
    <section data-testid="featured-suites-section" className="py-32 px-6 md:px-12 lg:px-24 bg-[#FAF8F5]">
      <motion.div
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
        variants={staggerContainer}
      >
        <motion.div variants={fadeInUp} className="text-center mb-16">
          <h2
            data-testid="suites-section-title"
            className="font-serif font-bold tracking-tight text-[#1E3A8A] mb-4"
            style={{ fontSize: '26px' }}
          >
            Choose Your Sanctuary
          </h2>
          <p
            data-testid="suites-section-subtitle"
            className="text-base leading-relaxed text-[#3A3A3A] max-w-2xl mx-auto"
          >
            Every apartment at De Azraa is a complete private residence — designed with intention, finished to perfection.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
          {suites.map((suite, index) => (
            <motion.div
              key={suite.id}
              variants={fadeInUp}
              data-testid={`suite-card-${index}`}
              className="group bg-[#FFFFFF] border border-[#E5E5E5] hover:border-[#C9A84C] transition-all duration-500 overflow-hidden"
            >
              <div className="relative h-80 overflow-hidden">
                <img
                  src={suite.image_url}
                  alt={suite.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  data-testid={`suite-image-${index}`}
                />
              </div>
              <div className="p-8">
                <h3
                  data-testid={`suite-name-${index}`}
                  className="font-serif text-2xl sm:text-3xl tracking-tight font-normal text-[#1A1A1A] mb-2"
                >
                  {suite.name}
                </h3>
                <p
                  data-testid={`suite-description-${index}`}
                  className="text-base leading-relaxed text-[#3A3A3A] mb-4"
                >
                  {suite.description.substring(0, 120)}...
                </p>
                <div className="flex items-center justify-between mb-6">
                  <span data-testid={`suite-price-${index}`} className="text-[#1A1A1A] font-medium">
                    From ₦{suite.price_per_night.toLocaleString()}/night
                  </span>
                </div>
                <Link
                  to={`/suites/${suite.id}`}
                  data-testid={`suite-view-link-${index}`}
                  className="inline-block border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAF8F5] transition-colors duration-300 px-8 py-3 text-sm tracking-widest uppercase w-full text-center"
                >
                  View Suite
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </section>
  );

  // Section 4: About/Story
  const AboutSection = () => (
    <section data-testid="about-section" className="py-32 px-6 md:px-12 lg:px-24 bg-[#FAF8F5]">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <img
            src="https://customer-assets.emergentagent.com/job_0902ddbc-a622-478c-8995-4613e012561c/artifacts/bi2ck6w2_image.png"
            alt="De Azraa Building"
            data-testid="about-building-image"
            className="w-full h-[600px] object-cover"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 60 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          data-testid="about-content"
        >
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#1A1A1A] mb-6">
            Built for Those Who Know the Difference.
          </h2>
          <p className="text-base leading-relaxed text-[#3A3A3A] mb-6">
            De Azraa Luxury Apartments was created for guests who expect more than a room — they expect an experience. Located in Udoka Housing Estate, Awka, each suite combines the privacy of a personal residence with the standards of five-star hospitality.
          </p>
          <p className="text-base leading-relaxed text-[#3A3A3A] mb-8">
            From the marble floors to the 24-hour power supply, every detail was chosen with you in mind.
          </p>
          <p className="font-serif text-2xl text-[#C9A84C] italic mb-8" data-testid="about-tagline">
            Pure Comfort. Timeless Luxury.
          </p>
          <Link
            to="/about"
            data-testid="about-learn-more-link"
            className="text-sm tracking-widest uppercase font-medium text-[#1A1A1A] hover:text-[#C9A84C] transition-colors inline-flex items-center gap-2"
          >
            Learn Our Story
            <ChevronDown className="rotate-[-90deg]" size={16} />
          </Link>
        </motion.div>
      </div>
    </section>
  );

  // Section 5: Amenities Highlight
  const AmenitiesSection = () => {
    const amenities = [
      {
        icon: ChefHat,
        title: 'Private Kitchenette',
        description: 'Fully equipped kitchen with modern appliances for your convenience.'
      },
      {
        icon: Zap,
        title: '24-Hr Power',
        description: 'Dedicated generator ensuring uninterrupted electricity supply.'
      },
      {
        icon: Wifi,
        title: 'Free WiFi',
        description: 'High-speed fiber internet for seamless connectivity.'
      },
      {
        icon: Shield,
        title: 'In-Room Safe',
        description: 'Electronic safe to secure your valuables with peace of mind.'
      },
      {
        icon: Tv,
        title: 'Smart TV',
        description: 'Premium entertainment with smart flat-screen television.'
      },
      {
        icon: Wind,
        title: 'Air Conditioning',
        description: 'Climate control for your ultimate comfort year-round.'
      }
    ];

    return (
      <section data-testid="amenities-section" className="py-32 px-6 md:px-12 lg:px-24 bg-[#F2EBE1]">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <h2
              data-testid="amenities-title"
              className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#1A1A1A] mb-4"
            >
              Everything You Need. Nothing You Don't.
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {amenities.map((amenity, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                data-testid={`amenity-card-${index}`}
                className="text-center p-8"
              >
                <amenity.icon className="text-[#C9A84C] mx-auto mb-4" size={32} strokeWidth={1} />
                <h3 className="text-xl font-medium text-[#1A1A1A] mb-2">{amenity.title}</h3>
                <p className="text-base leading-relaxed text-[#3A3A3A]">{amenity.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>
    );
  };

  // Section 6: Gallery Preview
  const GalleryPreview = () => {
    const galleryImages = [
      'https://customer-assets.emergentagent.com/job_azraa-luxury/artifacts/6uek5qa5_image.png',
      'https://customer-assets.emergentagent.com/job_azraa-luxury/artifacts/26e8bwp9_image.png',
      'https://customer-assets.emergentagent.com/job_0902ddbc-a622-478c-8995-4613e012561c/artifacts/bi2ck6w2_image.png',
      'https://images.unsplash.com/photo-1630377066297-de3f32924981?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3RlbCUyMGJhdGhyb29tJTIwbWFyYmxlfGVufDB8fHx8MTc3Nzk5NDE3NXww&ixlib=rb-4.1.0&q=85',
      'https://images.pexels.com/photos/30457600/pexels-photo-30457600.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      'https://images.pexels.com/photos/27299153/pexels-photo-27299153.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    ];

    return (
      <section data-testid="gallery-preview-section" className="py-32 px-6 md:px-12 lg:px-24 bg-[#FAF8F5]">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <h2
              data-testid="gallery-title"
              className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#1A1A1A] mb-12"
            >
              A Glimpse Inside
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {galleryImages.map((image, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                data-testid={`gallery-image-${index}`}
                className="relative h-80 overflow-hidden group"
              >
                <img
                  src={image}
                  alt={`De Azraa Interior ${index + 1}`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
              </motion.div>
            ))}
          </div>

          <motion.div variants={fadeInUp} className="text-center mt-12">
            <Link
              to="/gallery"
              data-testid="gallery-view-all-link"
              className="inline-block border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FAF8F5] transition-colors duration-300 px-12 py-4 text-sm tracking-widest uppercase"
            >
              View Full Gallery
            </Link>
          </motion.div>
        </motion.div>
      </section>
    );
  };

  // Section 7: Testimonials
  const TestimonialsSection = () => {
    const testimonials = [
      {
        quote: "I've stayed in hotels across Lagos and Abuja. De Azraa is genuinely different — it felt like I owned the place.",
        author: 'Chioma A.',
        location: 'Lagos'
      },
      {
        quote: 'The 24-hour power supply alone makes this worth every naira. Add the stunning interiors and you have perfection.',
        author: 'Emeka O.',
        location: 'Abuja'
      },
      {
        quote: 'Pure luxury. From the marble floors to the gold lighting, every detail screams class. Will be back soon.',
        author: 'Adaeze N.',
        location: 'Enugu'
      }
    ];

    return (
      <section data-testid="testimonials-section" className="py-32 px-6 md:px-12 lg:px-24 bg-[#1A1A1A]">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={staggerContainer}
        >
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <h2
              data-testid="testimonials-title"
              className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-white mb-4"
            >
              Guests Who've Felt the Difference
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                data-testid={`testimonial-card-${index}`}
                className="p-8 border border-white/10"
              >
                <div className="text-[#C9A84C] text-6xl font-serif mb-4">"</div>
                <p className="text-white text-base leading-relaxed mb-6">{testimonial.quote}</p>
                <div>
                  <p className="text-white font-medium">{testimonial.author}</p>
                  <p className="text-white/60 text-sm">{testimonial.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>
    );
  };

  // Section 8: Location
  const LocationSection = () => (
    <section data-testid="location-section" className="py-32 px-6 md:px-12 lg:px-24 bg-[#FAF8F5]">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, x: -60 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          data-testid="location-map"
          className="h-[500px]"
        >
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3964.2!2d7.0719!3d6.2103!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNsKwMTInMzcuMSJOIDfCsDA0JzE4LjgiRQ!5e0!3m2!1sen!2sng!4v1234567890"
            width="100%"
            height="100%"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            title="De Azraa Location"
          />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 60 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          data-testid="location-info"
          className="flex flex-col justify-center"
        >
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#1A1A1A] mb-8">
            Find Us in Awka
          </h2>

          <div className="space-y-6">
            <div className="flex items-start gap-4" data-testid="location-address">
              <MapPin className="text-[#C9A84C] flex-shrink-0 mt-1" size={24} strokeWidth={1.5} />
              <div>
                <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">Address</h3>
                <p className="text-base leading-relaxed text-[#3A3A3A]">
                  Plot 10/9 Udoka Housing Estate Phase 1<br />
                  Awka, Anambra State, Nigeria
                </p>
              </div>
            </div>

            <div className="flex items-start gap-4" data-testid="location-phone">
              <Phone className="text-[#C9A84C] flex-shrink-0 mt-1" size={24} strokeWidth={1.5} />
              <div>
                <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">Phone</h3>
                <a href="tel:+2348066722221" className="text-base text-[#3A3A3A] hover:text-[#C9A84C] transition-colors">
                  +234 806 672 2221
                </a>
              </div>
            </div>

            <div className="flex items-start gap-4" data-testid="location-hours">
              <Clock className="text-[#C9A84C] flex-shrink-0 mt-1" size={24} strokeWidth={1.5} />
              <div>
                <h3 className="text-xs tracking-[0.2em] uppercase font-semibold text-[#1A1A1A] mb-2">Hours</h3>
                <p className="text-base text-[#3A3A3A]">Open 24 Hours</p>
              </div>
            </div>

            <a
              href="https://wa.me/2348066722221"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-whatsapp-btn"
              className="inline-block bg-[#25D366] text-white hover:bg-[#1DA851] transition-colors duration-300 px-8 py-4 text-sm tracking-widest uppercase mt-4"
            >
              Chat on WhatsApp
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );

  // Section 9: Final CTA
  const FinalCTA = () => (
    <section
      data-testid="final-cta-section"
      className="relative py-32 px-6 md:px-12 lg:px-24 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[#C9A84C]/90" />
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_azraa-luxury/artifacts/6uek5qa5_image.png"
          alt="Suite interior"
          className="w-full h-full object-cover opacity-20"
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="relative z-10 text-center"
      >
        <h2
          data-testid="final-cta-title"
          className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight font-light text-[#1A1A1A] mb-8"
        >
          Your Suite Is Waiting.
        </h2>
        <Link
          to="/book"
          data-testid="final-cta-button"
          className="inline-block bg-[#1A1A1A] text-[#FAF8F5] hover:bg-white hover:text-[#1A1A1A] transition-colors duration-300 px-12 py-4 text-sm tracking-widest uppercase"
        >
          Book Now — It's That Simple
        </Link>
      </motion.div>
    </section>
  );

  return (
    <div data-testid="home-page">
      <HeroSection />
      <TrustStrip />
      <FeaturedSuites />
      <AboutSection />
      <AmenitiesSection />
      <GalleryPreview />
      <TestimonialsSection />
      <LocationSection />
      <FinalCTA />
    </div>
  );
};

export default Home;
